import React from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Lock, ArrowLeft } from 'lucide-react';

/**
 * Feature Not Enabled Component
 * 
 * Displays a consistent "Feature Not Enabled" message when a user 
 * visits a module that is disabled via feature flags.
 * 
 * This is used across all scaffolded future modules:
 * - Dispatch Automation
 * - Load Boards
 * - Payroll / Settlements Integration
 * - Advanced Analytics
 * - Driver Self-Service Dashboard
 */
const FeatureNotEnabled = ({ 
  featureName = 'This Feature',
  withLayout = true 
}) => {
  const navigate = useNavigate();

  const content = (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <Card className="border-gray-200 bg-white shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-gray-400" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3" data-testid="feature-not-enabled-title">
              Feature Not Enabled
            </h2>

            {/* Message */}
            <p className="text-gray-600 mb-6" data-testid="feature-not-enabled-message">
              This module is not currently active. Please contact an administrator for access.
            </p>

            {/* Feature Name Badge (optional context) */}
            {featureName && (
              <div className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-full mb-6">
                {featureName}
              </div>
            )}

            {/* Back Button */}
            <div>
              <Button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2 mx-auto"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  if (withLayout) {
    return <DashboardLayout>{content}</DashboardLayout>;
  }

  return content;
};

export default FeatureNotEnabled;
